import { Icon, Text, type IconName } from "../ds";
import { diaLegible, claveDia, estadoLead, fechaHora, hora, motivoDescarte, usd } from "../formato";
import type { DetalleLead, EstadoLead } from "../tipos";
import { motivoDemo, motivoEnvioDemo, motivoPedido, nombrePortal } from "./Demo";
import s from "./Actividad.module.css";

type Tono = "neutral" | "brand" | "success" | "accent" | "danger" | "warning";
interface Item {
  clave: string;
  cuando: string;
  icono: IconName;
  tono: Tono;
  titulo: string;
  texto?: string;
}

const FASE_DESHECHA: Record<string, string> = {
  info_enviada: "Info enviada",
  seguimiento: "Seguimiento",
  pidio_mas: "Le interesó la info",
  reunion: "Reunión agendada",
  cliente: "Es cliente",
  no_interesa: "No le interesa",
};

const txt = (x: unknown) => (typeof x === "string" && x.trim() ? x.trim() : null);
const por = (d: Record<string, unknown>) => {
  if (d.origen === "chatwoot") return d.por && d.por !== "chatwoot" ? ` Lo mandó ${d.por} desde Chatwoot.` : " Con una etiqueta en Chatwoot.";
  return txt(d.por) && d.por !== "local" ? ` Lo hizo ${d.por}.` : "";
};

/* Del historial crudo (eventos + fuentes) a una línea de tiempo legible. Los cambios de estado
 * que hizo una persona desde el panel llegan dos veces (el trigger de la base y el panel): se
 * muestra uno solo, con el motivo y quién. */
export function armarActividad(d: DetalleLead): Item[] {
  const items: Item[] = [];
  const panel = d.eventos.filter((e) => e.tipo === "panel_estado");

  for (const f of d.fuentes) {
    if (f.fuente === "google_places") {
      items.push({ clave: `f${f.id}`, cuando: f.primeraVez, icono: "search", tono: "neutral", titulo: "Encontrada en Google Maps", texto: f.zona ? `Búsqueda en la zona ${f.zona}.` : undefined });
    } else if (f.fuente === "manual") {
      // el evento alta_manual ya lo cuenta
    } else if (f.fuente !== "sitio_web") {
      const datos = (f.datos ?? {}) as { origen?: string; motivo?: string };
      items.push(
        datos.origen?.startsWith("listado_")
          ? {
              clave: `f${f.id}`,
              cuando: f.primeraVez,
              icono: "link",
              tono: "neutral",
              titulo: `Encontrada en ${nombrePortal(f.fuente)}`,
              texto: datos.motivo === "telefono" ? "En el listado de su zona, con su mismo teléfono." : "En el listado de su zona, con su mismo nombre.",
            }
          : datos.origen === "panel"
            ? { clave: `f${f.id}`, cuando: f.primeraVez, icono: "link", tono: "neutral", titulo: `Perfil de ${f.fuente} cargado a mano`, texto: por(datos as Record<string, unknown>).trim() || undefined }
            : { clave: `f${f.id}`, cuando: f.primeraVez, icono: "link", tono: "neutral", titulo: `Link a ${f.fuente} en su sitio` },
      );
    }
  }
  if (d.enriquecidaEn && d.sitio) {
    const hallado = [d.sitio.emails.length ? "email" : null, d.whatsapp ? "WhatsApp" : null, d.instagram ? "Instagram" : null].filter(Boolean);
    items.push({
      clave: "sitio",
      cuando: d.enriquecidaEn,
      icono: "globe",
      tono: "accent",
      titulo: d.sitio.error ? "No se pudo leer el sitio" : "Sitio leído",
      texto: d.sitio.error ? "Estaba caído o bloquea lecturas automáticas." : hallado.length ? `Encontró ${hallado.join(", ").replace(/, ([^,]*)$/, " y $1")}.` : "No encontró datos de contacto.",
    });
  }

  for (const e of d.eventos) {
    const x = e.datos ?? {};
    const base = { clave: `e${e.id}`, cuando: e.creadoEn };
    switch (e.tipo) {
      case "cambio_estado": {
        const a = x.a as EstadoLead;
        const hecho = panel.find((p) => p.datos.a === a && Math.abs(new Date(p.creadoEn).getTime() - new Date(e.creadoEn).getTime()) < 10_000);
        const motivo = hecho ? txt(hecho.datos.motivo) : null;
        items.push({
          ...base,
          icono: a === "descartada" || a === "baja" ? "x" : "check",
          tono: a === "descartada" || a === "baja" ? "danger" : a === "calificada" ? "brand" : "success",
          titulo: `Pasó a ${estadoLead(a).etiqueta.toLowerCase()}`,
          texto: [a === "calificada" && d.puntaje !== null ? `Puntaje ${d.puntaje} de 100.` : null, motivo ? `Motivo: ${motivo}.` : null, hecho ? por(hecho.datos).trim() : null].filter(Boolean).join(" ") || undefined,
        });
        break;
      }
      case "panel_estado":
        break;
      case "wa_borrador":
        items.push({ ...base, icono: "file-text", tono: "brand", titulo: "Borrador de WhatsApp listo", texto: String(x.generado_por ?? "").startsWith("claude") ? "Redactado con Claude." : "Armado desde la plantilla." });
        break;
      case "wa_chatwoot":
        items.push({
          ...base,
          icono: "whatsapp",
          tono: "brand",
          titulo: "WhatsApp enviado por Chatwoot",
          texto: `${x.modo === "plantilla" ? (txt(x.plantilla) ? `Con la plantilla ${txt(x.plantilla)}.` : "Como plantilla de Meta.") : "Como texto libre."}${por(x)}`,
        });
        break;
      case "wa_marcado_enviado":
        items.push({ ...base, icono: "whatsapp", tono: "brand", titulo: "WhatsApp marcado como enviado", texto: `Se mandó fuera del panel.${por(x)}` });
        break;
      case "wa_enviado":
        break; // lo cuentan wa_chatwoot o wa_marcado_enviado
      case "wa_descartado":
        items.push({ ...base, icono: "trash-2", tono: "neutral", titulo: "Borrador de WhatsApp descartado", texto: por(x).trim() || undefined });
        break;
      case "wa_restaurado":
        items.push({ ...base, icono: "rotate-ccw", tono: "neutral", titulo: "Borrador de WhatsApp restaurado", texto: por(x).trim() || undefined });
        break;
      case "wa_editado":
        items.push({ ...base, icono: "pencil", tono: "neutral", titulo: "Texto del WhatsApp editado", texto: por(x).trim() || undefined });
        break;
      case "respuesta":
        items.push({
          ...base,
          icono: "message-circle",
          tono: "success",
          titulo: x.canal === "whatsapp" ? "Respondió por WhatsApp" : "Respondió",
          texto: txt(x.texto) ? `“${txt(x.texto)}”` : txt(x.nota) ?? undefined,
        });
        break;
      case "info_enviada":
        items.push({ ...base, icono: "send", tono: "success", titulo: "Interesado, se le mandó la info", texto: [txt(x.nota), por(x).trim()].filter(Boolean).join(" ") || undefined });
        break;
      case "seguimiento":
        items.push({ ...base, icono: "refresh-cw", tono: "neutral", titulo: "En seguimiento", texto: [txt(x.nota), por(x).trim()].filter(Boolean).join(" ") || undefined });
        break;
      case "pidio_mas":
        items.push({ ...base, icono: "sparkles", tono: "success", titulo: "Le interesó la info", texto: [txt(x.nota), por(x).trim()].filter(Boolean).join(" ") || undefined });
        break;
      case "reunion":
        items.push({
          ...base,
          icono: "calendar-check",
          tono: "success",
          titulo: "Reunión agendada",
          texto: [txt(x.fecha) ? `Para el ${fechaHora(String(x.fecha))}.` : null, txt(x.nota), por(x).trim()].filter(Boolean).join(" ") || undefined,
        });
        break;
      case "fase_deshecha":
        items.push({ ...base, icono: "rotate-ccw", tono: "neutral", titulo: `Se deshizo “${FASE_DESHECHA[String(x.tipo)] ?? String(x.tipo)}”`, texto: por(x).trim() || undefined });
        break;
      case "cliente":
        items.push({
          ...base,
          icono: "badge-check",
          tono: "success",
          titulo: "Pasó a ser cliente",
          texto: [typeof x.cobrado_usd === "number" ? `Cash collected: ${usd(x.cobrado_usd)}.` : null, txt(x.nota), por(x).trim()].filter(Boolean).join(" ") || undefined,
        });
        break;
      case "cobro":
        items.push({
          ...base,
          icono: "trending-up",
          tono: "success",
          titulo: "Cash collected actualizado",
          texto: `De ${usd(Number(x.de ?? 0))} a ${usd(Number(x.a ?? 0))}.${por(x)}`,
        });
        break;
      case "no_interesa":
        items.push({ ...base, icono: "user-x", tono: "danger", titulo: "No le interesa", texto: [txt(x.nota), "Queda fuera de todo contacto."].filter(Boolean).join(" ") });
        break;
      case "baja":
        items.push({ ...base, icono: "user-x", tono: "danger", titulo: "Pidió la baja", texto: txt(x.motivo) ? `Motivo: ${motivoDescarte(String(x.motivo))}.` : undefined });
        break;
      case "nota":
        items.push({ ...base, icono: "pencil", tono: "neutral", titulo: txt(x.texto) ? "Nota actualizada" : "Nota borrada", texto: txt(x.texto) ?? undefined });
        break;
      case "alta_manual":
        items.push({ ...base, icono: "user-plus", tono: "brand", titulo: "Agregada a mano", texto: por(x).trim() || undefined });
        break;
      case "demo_armada": {
        // Las demos de antes de sumar portales no anotaban el portal: eran todas de Zonaprop.
        const de = nombrePortal(txt(x.portal) ?? "zonaprop");
        items.push({ ...base, icono: "sparkles", tono: "brand", titulo: "Demo armada", texto: txt(x.titulo) ? `Con su aviso “${txt(x.titulo)}” de ${de}.` : `Con un aviso suyo de ${de}.` });
        break;
      }
      case "demo_fallida":
        items.push({ ...base, icono: "info", tono: "neutral", titulo: "No se pudo armar la demo", texto: motivoDemo(x.motivo) });
        break;
      case "demo_solicitada":
        if (x.origen === "boton") {
          items.push({ ...base, icono: "sparkles", tono: "brand", titulo: "Pidió la demo con el botón", texto: `Tocó “${txt(x.boton) ?? "sí"}”. Se le manda sola a los 5-10 minutos.` });
        } else {
          items.push({ ...base, icono: "sparkles", tono: "neutral", titulo: x.otra_propiedad ? "Pidió otra propiedad para la demo" : "Pidió la demo", texto: por(x).trim() || undefined });
        }
        break;
      case "demo_envio":
        if (x.resultado === "enviada") items.push({ ...base, icono: "send", tono: "success", titulo: "Demo enviada por WhatsApp" });
        else if (x.resultado === "manual") {
          items.push({ ...base, icono: "info", tono: "warning", titulo: "La demo quedó para mandar a mano", texto: `${motivoEnvioDemo(x.motivo)} En Chatwoot tiene la etiqueta demo-manual.` });
        }
        break;
      case "demo_pedido_atendido":
        // Lo armado y lo que no tenía aviso ya lo cuentan demo_armada y demo_fallida.
        if (x.resultado === "error") items.push({ ...base, icono: "info", tono: "warning", titulo: "No se pudo armar la demo pedida", texto: motivoPedido("error", txt(x.motivo)) });
        break;
      case "demo_abierta":
        items.push({ ...base, icono: "eye", tono: "success", titulo: "Abrió su demo", texto: Number(x.visita) > 1 ? `Es la visita número ${Number(x.visita)}.` : "Es la primera vez." });
        break;
      case "demo_vencida":
        items.push({ ...base, icono: "clock", tono: "neutral", titulo: "Venció la demo", texto: Number(x.visitas) ? `La abrió ${Number(x.visitas) === 1 ? "una vez" : `${Number(x.visitas)} veces`}.` : "No llegó a abrirla." });
        break;
      case "email_enviado":
        items.push({ ...base, icono: "mail", tono: "brand", titulo: "Email enviado" });
        break;
      case "email_delivered":
        items.push({ ...base, icono: "mail-check", tono: "neutral", titulo: "Email entregado" });
        break;
      case "email_opened":
        items.push({ ...base, icono: "eye", tono: "accent", titulo: "Abrió el email" });
        break;
      case "email_bounced":
        items.push({ ...base, icono: "mail-x", tono: "danger", titulo: "El email rebotó", texto: "La dirección no existe o no acepta correo. Queda fuera de los envíos." });
        break;
      case "email_complained":
        items.push({ ...base, icono: "mail-x", tono: "danger", titulo: "Marcó el email como spam" });
        break;
      default:
        items.push({ ...base, icono: "info", tono: "neutral", titulo: e.tipo.replace(/_/g, " ") });
    }
  }
  return items.sort((a, b) => b.cuando.localeCompare(a.cuando));
}

export function Actividad({ detalle }: { detalle: DetalleLead }) {
  const items = armarActividad(detalle);
  const dias: { dia: string; items: Item[] }[] = [];
  for (const it of items) {
    const clave = claveDia(it.cuando);
    const ultimo = dias[dias.length - 1];
    if (ultimo && claveDia(ultimo.items[0]!.cuando) === clave) ultimo.items.push(it);
    else dias.push({ dia: diaLegible(it.cuando), items: [it] });
  }
  return (
    <div className={s.actividad}>
      {dias.map((d) => (
        <section key={d.dia} className={s.dia}>
          <h3 className={s.fecha}>{d.dia}</h3>
          <ol className={s.lista}>
            {d.items.map((it, i) => (
              <li key={it.clave} className={s.item}>
                <div className={s.riel}>
                  <span className={s.icono} data-tono={it.tono}>
                    <Icon name={it.icono} size={14} />
                  </span>
                  {i < d.items.length - 1 ? <span className={s.linea} /> : null}
                </div>
                <div className={s.contenido}>
                  <div className={s.encabezado}>
                    <Text as="span" size="sm" weight="semibold" tone="strong">
                      {it.titulo}
                    </Text>
                    <Text as="span" size="xs" tone="muted" mono>
                      {hora(it.cuando)}
                    </Text>
                  </div>
                  {it.texto ? (
                    <Text as="span" size="xs" className={s.texto}>
                      {it.texto}
                    </Text>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
